import { memo } from "react";

import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion.tsx";
import { cn } from "@/lib/utils";
import { ColorMap, Dataset } from "@/types/dataset";
import { isRankableAttribute } from "@/utils/map/rank";

import ColorBasedSelect from "../ColorBasedSelect";
import ColorScaleSelect from "../ColorScaleSelect";
import OpacitySlider from "../OpacitySlider";
import { LabelSettings } from "./LabelSettings";
import { RankSettings } from "./RankSettings";
import StrokeWidthSlider from "./StrokeWidthSlider";

const TYPE_LABEL = {
  fill: "Fill",
  label: "Label",
  stroke: "Stroke"
};

interface CategoryConfig {
  value: string;
  onChange?: (value: string) => void;
  title?: string;
}

interface ColorConfig {
  value: ColorMap;
  onChange: (value: ColorMap) => void;
  step?: number;
  onStepChange?: (step: number) => void;
}

interface RankConfig {
  onChangeSlider?: (value: number[]) => void;
  onChangeInput?: (e: number) => void;
  onCheck?: (value: boolean) => void;
}

interface StrokeConfig {
  value: number;
  onChange: (value: number) => void;
}

interface LabelConfig {
  show?: boolean;
  onCheck?: (value: boolean) => void;
}

interface DatasetStyleSelectionProps {
  type: "fill" | "label" | "stroke";
  data: Dataset;
  totalRows?: number;
  isEditMode?: boolean;
  isSpatialAnalysis?: boolean;
  isLayerVisible?: boolean;
  isNewUIDatasetConfigEnabled?: boolean;
  isRankingLabelEnabled?: boolean;
  categoryConfig: CategoryConfig;
  colorConfig: ColorConfig;
  labelConfig?: LabelConfig;
  rankConfig?: RankConfig;
  strokeConfig?: StrokeConfig;
}

export const DatasetStyleSelection = memo(
  (props: DatasetStyleSelectionProps) => {
    const {
      type,
      data,
      totalRows = 0,
      isEditMode,
      isSpatialAnalysis,
      isLayerVisible,
      isNewUIDatasetConfigEnabled,
      isRankingLabelEnabled,
      categoryConfig,
      colorConfig,
      labelConfig,
      rankConfig,
      strokeConfig
    } = props;

    const schema =
      data.data.schema.length > 0 ? data.data.schema : data.categories;

    const isRankable =
      type === "label" &&
      isRankableAttribute(schema, data.style.labelConfig.attribute_id);

    // Render condition flags
    const shouldShowLabelSettings = type === "label" && labelConfig;
    const shouldShowRankSettings =
      type === "label" && isRankingLabelEnabled && rankConfig;
    const shouldShowStrokeWidthSlider = type === "stroke" && strokeConfig;

    // Disabled state for fill type on line datasets
    const isDisabled =
      (type === "fill" && data.properties.spatialType === "line") ||
      (type === "stroke" && data.properties.spatialType !== "line");

    return (
      <AccordionItem value={type} aria-disabled={isDisabled}>
        <div
          className={cn(
            "border rounded-xl px-3",
            isDisabled && "bg-whisper pointer-events-none"
          )}
        >
          <div className="flex justify-between items-center">
            <p
              className={cn(
                "text-txtHeading text-[12px] font-semibold",
                isDisabled && "text-light-gray"
              )}
            >
              {TYPE_LABEL[type]}
            </p>
            <AccordionTrigger
              className="h-[30px]"
              data-testid={`expand-dataset-trigger-${type}`}
              disabled={isDisabled}
            />
          </div>
          <AccordionContent className="flex flex-col gap-2 pt-2">
            {/* Always visible components */}
            <ColorBasedSelect
              disabled={!isLayerVisible}
              value={categoryConfig.value}
              onValueChange={categoryConfig.onChange}
              categories={data.categories}
              title={categoryConfig.title}
            />

            {/* Label-specific components */}
            {shouldShowLabelSettings && (
              <LabelSettings
                disabled={!isLayerVisible}
                showLabel={labelConfig.show || false}
                onCheckDisplayLabel={labelConfig.onCheck}
              />
            )}

            {/* Edit mode components */}
            {isEditMode && (
              <>
                <ColorScaleSelect
                  value={colorConfig.value}
                  onValueChange={colorConfig.onChange}
                  type={type as "fill" | "stroke"}
                  stepValue={colorConfig.step}
                  onStepValueChange={colorConfig.onStepChange}
                  isNewUIDatasetConfigEnabled={isNewUIDatasetConfigEnabled}
                />
                <OpacitySlider
                  data={data}
                  isSpatialAnalysis={isSpatialAnalysis}
                  isLabel={type === "label"}
                />
              </>
            )}

            {/* Label-specific: Rank settings */}
            {shouldShowRankSettings && (
              <RankSettings
                disabled={!isLayerVisible}
                isEnabled={data.style.rankConfig?.isEnabled}
                isRankable={isRankable}
                onCheckRank={rankConfig.onCheck}
                onChangeSlider={rankConfig.onChangeSlider}
                onChangeInput={rankConfig.onChangeInput}
                data={data}
                totalRows={totalRows}
              />
            )}

            {shouldShowStrokeWidthSlider && (
              <StrokeWidthSlider
                value={strokeConfig.value}
                onChange={strokeConfig.onChange}
              />
            )}
          </AccordionContent>
        </div>
      </AccordionItem>
    );
  }
);

DatasetStyleSelection.displayName = "DatasetStyleSelection";
